import * as React from 'react';
import {
  requiredExternals,
  requiredServices,
  optionalServices,
  customOptionalServices,
  ChargeTimeSimulatorFeatureApp,
  ChargeTimeSimulatorFeatureAppConfig,
  ChargeTimeSimulatorFeatureAppDependencies,
} from '@volkswagen-onehub/whitelabel-e-mobility-chargetime-feature-app';
import {
  BrandThemeProvider,
  ComponentsLibrary,
  createEMobilityToolsFeatureAppTheme,
  EMobilityFeatureAppWrapper,
} from '@volkswagen-onehub/vw-e-mobility-components';
import {FeatureAppDefinition} from '@feature-hub/core';
import {ReactFeatureApp} from '@feature-hub/react';
import {
  devConfig,
  fetchBffConfig,
  getVwTrack,
  optionalVwServices,
  RequiredVwFeatureServices,
  requiredVwServices,
  VwFeatureServiceIds,
} from '@volkswagen-onehub/vw-e-mobility-feature-app-commons';
import {
  CommonFeatureServices,
  ChargetimeFeatureAppContentConfig,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {getChargeTimeTrackingFunctions} from './chargetime-tracking';
import {ThemeContext} from 'styled-components';
import {SimulatorFeatureAppName} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';

// Write debug data to global object

if (!window.eMobilityTools) {
  window.eMobilityTools = {};
}
window.eMobilityTools.chargetime = {
  version: process.env.APP_VERSION || 'unknown',
};

const featureAppDefinition: FeatureAppDefinition<
  ReactFeatureApp,
  ChargeTimeSimulatorFeatureAppDependencies & RequiredVwFeatureServices,
  ChargeTimeSimulatorFeatureAppConfig
> = {
  dependencies: {
    externals: requiredExternals,
    featureServices: {
      ...requiredVwServices,
      ...requiredServices,
    },
  },
  optionalDependencies: {
    featureServices: {
      ...optionalVwServices,
      ...optionalServices,
      ...customOptionalServices,
    },
  },
  create: function (env): ReactFeatureApp {
    const localeService =
      env.featureServices[CommonFeatureServices.localeService];

    const appDataPromise = fetchBffConfig(
      SimulatorFeatureAppName.Chargetime,
      {
        localeService,
        dataVersion:
          env.featureServices[VwFeatureServiceIds.dataVersionService],
      },
      env.featureServices[VwFeatureServiceIds.serviceConfigEndpointProvider],
      env.baseUrl
    );

    const loadingPromise = appDataPromise.then(() => undefined);

    const vwTrack = getVwTrack(
      env.featureServices[VwFeatureServiceIds.trackingManager],
      localeService
    );

    const trackingFunctions = getChargeTimeTrackingFunctions(vwTrack);

    const appDataContent = env.featureServices[
      VwFeatureServiceIds.contentService
    ].content as ChargetimeFeatureAppContentConfig;

    return {
      loadingPromise,
      render() {
        return (
          <BrandThemeProvider theme="main">
            <ThemeContext.Consumer>
              {(brandTheme) => (
                <EMobilityFeatureAppWrapper>
                  <ChargeTimeSimulatorFeatureApp
                    componentsLibrary={ComponentsLibrary}
                    theme={createEMobilityToolsFeatureAppTheme(brandTheme)}
                    featureAppEnvironment={env}
                    appDataPromise={appDataPromise}
                    appDataContent={appDataContent}
                    trackingFunctions={trackingFunctions}
                    externallyRenderedDisclaimerKind="module-reference"
                    devConfig={devConfig}
                  />
                </EMobilityFeatureAppWrapper>
              )}
            </ThemeContext.Consumer>
          </BrandThemeProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
